/** SUNTRONIX — Product 360 → OEM Product Intelligence panel.
 *  Shows the OEM source header, key configuration, full categorized specs,
 *  datasheet/source links, and a Check-OEM-Updates field-level diff with
 *  per-field Accept / Lock. Technical truth only — no commercial data. */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ExternalLink, FileText, ChevronDown, RefreshCw, Lock, CheckCircle2, Globe, Copy,
} from "lucide-react";

const renderBold = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);

const SYNC_TONE = {
  SYNCED: "bg-emerald-50 border-emerald-200 text-emerald-700",
  UPDATE_AVAILABLE: "bg-amber-50 border-amber-200 text-amber-700",
  UP_TO_DATE: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

const KEY_FIELDS = [
  ["processor", "Processor"], ["memory", "Memory"], ["storage", "Storage"],
  ["gpu", "Graphics"], ["display_size", "Display"], ["operating_system", "OS"],
  ["wireless", "Wireless"], ["battery", "Battery"], ["warranty", "Warranty"],
];

export default function OemIntelligencePanel({ product, onChanged }) {
  const s = product.oem_spec;
  const [showAll, setShowAll] = useState(false);
  const [checking, setChecking] = useState(false);
  const [diff, setDiff] = useState(null);        // {status, changes:[]}
  const [accept, setAccept] = useState({});      // field -> {accept, lock}
  const n = s.normalised || {};
  const cats = product.spec_categories || s.categories || [];
  const [piPath, setPiPath] = useState(null);
  useEffect(() => {
    api.get(`/oem/pi-token/${product.id}`).then(({ data }) => setPiPath(data.path)).catch(() => {});
  }, [product.id]);
  const piUrl = piPath ? `${window.location.origin}${piPath}` : "";

  const checkUpdates = async () => {
    setChecking(true); setDiff(null);
    try {
      const { data } = await api.post(`/oem/check-updates/${product.id}`);
      setDiff(data);
      if (data.status === "UPDATE_AVAILABLE") {
        setAccept(Object.fromEntries((data.changes || []).map((c) => [c.field, { accept: !c.locked, lock: false }])));
        toast.info(`${data.changes.length} field difference(s) found`);
      } else if (data.status === "UP_TO_DATE") toast.success("Product is up to date with the OEM source");
      else toast.warning(data.status.replace(/_/g, " "));
    } catch (e) { toast.error(e.response?.data?.detail || "Update check failed"); }
    setChecking(false);
  };

  const applyUpdates = async () => {
    const fields = Object.entries(accept).filter(([, v]) => v.accept).map(([f]) => f);
    const lock_fields = Object.entries(accept).filter(([, v]) => v.lock).map(([f]) => f);
    try {
      await api.post(`/oem/accept-updates/${product.id}`, { fields, lock_fields });
      toast.success("OEM updates applied");
      setDiff(null); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply updates"); }
  };

  const copyPi = () => { navigator.clipboard?.writeText(piUrl); toast.success("Product Intelligence link copied"); };

  return (
    <div className="bg-white border border-indigo-100 rounded-lg overflow-hidden" data-testid="oem-intel-panel">
      <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">
              <Sparkles className="h-3.5 w-3.5" /> Product Intelligence
            </div>
            <div className="mt-0.5 text-[12px] text-slate-600">
              Technical source: <b>{s.source}</b> · {s.connector_version}
              {s.region && <> · <Badge variant="outline" className="text-[10px]">{s.region}</Badge></>}
            </div>
            <div className="mt-0.5 text-[10px] text-slate-400" data-testid="oem-intel-meta">
              Last synced {(s.last_synced || "").slice(0, 16).replace("T", " ") || "—"}
              {s.last_checked && <> · checked {(s.last_checked).slice(0, 16).replace("T", " ")}</>}
              {" · confidence "}{s.confidence || "EXACT"}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge className={(SYNC_TONE[s.sync_status] || "bg-slate-100 border-slate-200 text-slate-600") + " text-[10px]"}
              data-testid="oem-intel-sync-status">{(s.sync_status || "SYNCED").replace(/_/g, " ")}</Badge>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.source_url && (
            <a href={s.source_url} target="_blank" rel="noreferrer" data-testid="oem-intel-source"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
              <ExternalLink className="h-3 w-3" /> Open OEM source
            </a>
          )}
          {s.datasheet?.url && (
            <a href={s.datasheet.url} target="_blank" rel="noreferrer" data-testid="oem-intel-datasheet"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
              <FileText className="h-3 w-3" /> Datasheet
            </a>
          )}
          {piUrl && (
            <a href={piUrl} target="_blank" rel="noreferrer" data-testid="oem-intel-client-page"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
              <Globe className="h-3 w-3" /> Client page
            </a>
          )}
          <button onClick={copyPi} disabled={!piUrl} data-testid="oem-intel-copy-link"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-40">
            <Copy className="h-3 w-3" /> Copy link
          </button>
          <button onClick={checkUpdates} disabled={checking} data-testid="oem-intel-check-updates"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700">
            <RefreshCw className={`h-3 w-3 ${checking ? "animate-spin" : ""}`} /> Check OEM updates
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Update diff */}
        {diff?.status === "UPDATE_AVAILABLE" && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="oem-intel-diff">
            <div className="text-[12px] font-semibold text-amber-800 mb-2">OEM update available — review field changes</div>
            <div className="space-y-1.5">
              {diff.changes.map((c) => (
                <div key={c.field} className="grid grid-cols-[1fr_auto] gap-2 items-center text-[11px] bg-white rounded border border-slate-200 px-2 py-1.5" data-testid={`oem-diff-${c.field}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-700 uppercase text-[9px] tracking-wide">{c.field.replace(/_/g, " ")}{c.locked && <Lock className="inline h-2.5 w-2.5 ml-1 text-slate-400" />}</div>
                    <div className="text-slate-400 line-through truncate">{c.current || "—"}</div>
                    <div className="text-emerald-700 truncate">{c.latest || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <label className="flex items-center gap-1 text-slate-600">
                      <input type="checkbox" disabled={c.locked} checked={!!accept[c.field]?.accept}
                        data-testid={`oem-diff-accept-${c.field}`}
                        onChange={(e) => setAccept((a) => ({ ...a, [c.field]: { ...a[c.field], accept: e.target.checked } }))} />
                      Accept
                    </label>
                    <label className="flex items-center gap-1 text-slate-600">
                      <input type="checkbox" checked={!!accept[c.field]?.lock}
                        data-testid={`oem-diff-lock-${c.field}`}
                        onChange={(e) => setAccept((a) => ({ ...a, [c.field]: { ...a[c.field], lock: e.target.checked } }))} />
                      Lock
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={applyUpdates} data-testid="oem-diff-apply" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px]">Apply selected</Button>
              <Button size="sm" variant="outline" onClick={() => setDiff(null)} className="h-7 text-[11px]">Dismiss</Button>
            </div>
          </div>
        )}
        {diff?.status === "UP_TO_DATE" && (
          <div className="text-[12px] text-emerald-700 flex items-center gap-1.5" data-testid="oem-intel-uptodate">
            <CheckCircle2 className="h-4 w-4" /> Up to date with {s.source}.
          </div>
        )}

        {/* Smart description */}
        {s.smart_description && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Quotation description</div>
            <div className="text-[12px] leading-relaxed text-slate-600 whitespace-pre-wrap" data-testid="oem-intel-desc">
              {renderBold(s.smart_description)}
            </div>
          </div>
        )}

        {/* Key configuration */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="oem-intel-key">
          {KEY_FIELDS.filter(([k]) => n[k]).map(([k, l]) => (
            <div key={k} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-wide text-slate-400">{l}</div>
              <div className="text-[11px] text-slate-700 line-clamp-2">{n[k]}</div>
            </div>
          ))}
        </div>

        {/* Full specs */}
        {cats.length > 0 && (
          <>
            <button onClick={() => setShowAll((v) => !v)} data-testid="oem-intel-toggle-full"
              className="flex items-center gap-1 text-[12px] text-indigo-600 font-medium">
              <ChevronDown className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
              {showAll ? "Hide" : "View"} complete specifications
            </button>
            {showAll && (
              <div className="space-y-2" data-testid="oem-intel-full">
                {cats.map((c) => (
                  <div key={c.category} className="rounded-lg border border-slate-200">
                    <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-600 rounded-t-lg">{c.category}</div>
                    <div className="divide-y divide-slate-100">
                      {(c.items || []).map((it, i) => (
                        <div key={i} className="px-3 py-1.5 grid grid-cols-[130px_1fr] gap-2 text-[11px]">
                          <div className="text-slate-400">{it.label}</div>
                          <div className="text-slate-700 whitespace-pre-wrap">{it.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="text-[10px] text-slate-400">Specifications sourced from {s.source}. Commercial data is SUNTRONIX-owned.</div>
      </div>
    </div>
  );
}
